/**
 * SiblingComparisonTable — full performance comparison of all creatives
 * sharing the same brief_id+angle_id. Rendered in the Performance tab.
 * The current creative row is highlighted; clicking a non-current row
 * navigates to that sibling's workspace.
 */

import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import type { CreativeSiblingSummary } from './types';

interface Props {
  siblings: CreativeSiblingSummary[];
  currentCreativeId: string;
}

const STATUS_COLOR: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  paused: 'bg-amber-100 text-amber-700 border-amber-200',
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  completed: 'bg-blue-100 text-blue-700 border-blue-200',
  archived: 'bg-slate-100 text-slate-400 border-slate-200',
  enriched: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  extracted: 'bg-blue-100 text-blue-700 border-blue-200',
  fetched: 'bg-slate-100 text-slate-600 border-slate-200',
  discovered: 'bg-slate-100 text-slate-600 border-slate-200',
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLOR[status] ?? STATUS_COLOR.draft;
  return (
    <span
      className={`inline-flex items-center text-[9px] font-mono px-1.5 py-0.5 rounded border uppercase ${cls}`}
    >
      {status}
    </span>
  );
}

export function SiblingComparisonTable({ siblings, currentCreativeId }: Props) {
  const navigate = useNavigate();
  if (siblings.length <= 1) return null;

  const currentRoas =
    siblings.find((s) => s.creative_id === currentCreativeId || s.is_current)?.lifetime_roas ?? 0;

  return (
    <section className="space-y-2">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        Brief Siblings ({siblings.length} creatives, same angle)
      </p>

      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-slate-400 w-12"></th>
              <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-slate-400">
                Angle / Creative
              </th>
              <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-slate-400">
                Spend
              </th>
              <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-slate-400">
                ROAS
              </th>
              <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-slate-400">
                Score
              </th>
              <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-slate-400">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {siblings.map((sibling) => {
              const isCurrent = sibling.creative_id === currentCreativeId || sibling.is_current;
              return (
                <tr
                  key={sibling.creative_id}
                  className={clsx(
                    'hover:bg-slate-50 transition-colors',
                    isCurrent ? 'bg-teal-50/40' : 'cursor-pointer',
                  )}
                  onClick={() => {
                    if (!isCurrent) {
                      navigate(
                        `/creative/assets/creative/${encodeURIComponent(sibling.creative_id)}`,
                      );
                    }
                  }}
                >
                  <td className="px-3 py-2">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                      {sibling.thumbnail_url && /^https?:/i.test(sibling.thumbnail_url) ? (
                        <img
                          src={sibling.thumbnail_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-100" />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="space-y-0.5">
                      {sibling.angle_name && (
                        <p className="text-[10px] font-mono text-teal-600">
                          {sibling.angle_name}
                        </p>
                      )}
                      <p
                        className={clsx(
                          'font-medium truncate max-w-[200px]',
                          isCurrent ? 'text-teal-700' : 'text-navy-900',
                        )}
                      >
                        {sibling.headline || sibling.creative_id.slice(-8)}
                        {isCurrent && (
                          <span className="ml-1.5 text-[9px] font-mono text-teal-500 uppercase">
                            ← this
                          </span>
                        )}
                      </p>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-slate-700">
                    {sibling.lifetime_spend != null
                      ? `$${sibling.lifetime_spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {sibling.lifetime_roas != null ? (
                      <span
                        className={clsx(
                          'font-mono font-semibold',
                          isCurrent
                            ? 'text-teal-700'
                            : sibling.lifetime_roas > currentRoas
                              ? 'text-emerald-600'
                              : 'text-slate-600',
                        )}
                      >
                        {sibling.lifetime_roas.toFixed(2)}×
                      </span>
                    ) : (
                      <span className="text-slate-400 font-mono">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {sibling.review_score != null ? (
                      <span
                        className={clsx(
                          'font-mono font-semibold',
                          sibling.review_verdict === 'ready'
                            ? 'text-emerald-600'
                            : sibling.review_verdict === 'needs_work'
                              ? 'text-amber-600'
                              : sibling.review_verdict === 'critical'
                                ? 'text-rose-600'
                                : 'text-slate-600',
                        )}
                      >
                        {sibling.review_score.toFixed(1)}/10
                      </span>
                    ) : (
                      <span className="text-slate-400 font-mono">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <StatusBadge status={sibling.status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
